import { useState, useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, Remove, HelpOutline } from "@mui/icons-material";
import UndoIcon from "@mui/icons-material/Undo";
import { EventContext } from "../../../contexts/EventContext";
import { MatchContext } from "../../../contexts/MatchContext";
import request from "../../../utilities/requests";

export default function CreateMatches() {
  const { pairingEvent, userData, pairingData, setPairingData } =
    useContext(EventContext);
  const { filters, setFilters, createMatches } = useContext(MatchContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonTitle, setButtonTitle] = useState("Create Matches");
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [ageFlex, setAgeFlex] = useState(10);
  const [anchor, setAnchor] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showPrevMatches, setShowPrevMatches] = useState(false);
  const [showMaximizeDates, setShowMaximizeDates] = useState(false);
  const [maximizeDates, setMaximizeDates] = useState(false);
  const [prevMatches, setPrevMatches] = useState(false);
  const [diableCreate, setDisableCreate] = useState(false);

  useEffect(() => {
    if (pairingEvent?.pairingEventType === "mixer") {
      setButtonTitle("Calc Match Scores");
    } else if (pairingEvent?.pairingEventType === "dinner") {
      if (pairingEvent?.event?.status === "scheduled") {
        setButtonTitle("Create Tables");
      } else {
        setButtonTitle("Calc Match Scores");
      }
    } else if (pairingEvent?.event?.status === "active") {
      setButtonTitle("Refresh Matches");
    } else {
      setButtonTitle("Create Matches");
    }

    if (
      pairingEvent?.event?.status === "active" &&
      pairingEvent?.pairingEventType !== "dinner"
    ) {
      const isPastHalfway =
        pairingEvent?.currentRound > pairingEvent?.numberOfRounds / 2;
      if (isPastHalfway) {
        setShowRefreshWarning(true);
      } else {
        setShowRefreshWarning(false);
      }
    }
  }, [pairingEvent]);

  useEffect(() => {
    if (pairingEvent?.event?.status === "completed") {
      setDisableCreate(true);
    } else {
      setDisableCreate(false);
    }
  }, [pairingEvent]);

  useEffect(() => {
    if (userData?.org?.config?.allowPreviousMatches) {
      setShowPrevMatches(true);
    }
    if (userData?.org?.config?.showMaximizeDates) {
      setShowMaximizeDates(true);
    }
  }, [userData]);

  const handleHelpClick = () => {
    setHelpOpen(!helpOpen);
  };

  const handleIncrease = () => setAgeFlex((prev) => prev + 1);
  const handleDecrease = () => setAgeFlex((prev) => Math.max(prev - 1, 0)); // Prevent negative values

  const handleFlexChange = (action, e) => {
    let flex = ageFlex;
    if (action === "add") {
      flex++;
    } else {
      flex--;
    }
    setAgeFlex(flex);
    let updatedFilters = filters.map((f) => {
      if (f?.variant === "age") {
        return { ...f, ageFlex: flex };
      } else {
        return f;
      }
    });
    setFilters([...updatedFilters]);
  };

  const renderAgeFilter = (filter, i) => {
    return (
      <Box key={filter._id} display="flex" alignItems="center">
        <FormControlLabel
          key={filter._id}
          control={
            <Checkbox
              checked={filter.checked ? filter.checked : false}
              onChange={() => handleCheck(i)}
            />
          }
          label={filter.label}
        />
        <IconButton onClick={(e) => handleFlexChange("remove", e)} size="small">
          <Remove />
        </IconButton>
        <TextField
          size="small"
          type="number"
          disabled
          value={ageFlex}
          // onChange={handleFlexChange}
          inputProps={{ min: 0, style: { textAlign: "center" } }} // Prevent negative input
          sx={{ width: 75, mx: 1 }}
        />
        <IconButton onClick={(e) => handleFlexChange("add", e)} size="small">
          <Add />
        </IconButton>
      </Box>
    );
  };

  const handleCheck = (i) => {
    if (!filters[i].checked) {
      filters[i].checked = true;
    } else {
      filters[i].checked = false;
    }

    setFilters([...filters]);
  };

  const handleCreate = async () => {
    setLoading(true);
    setOpen(false);
    let update = pairingEvent?.event?.status === "active" ? true : false;
    await createMatches(update, maximizeDates, prevMatches);
    setLoading(false);
  };

  const handleButtonClick = async () => {
    if (
      pairingEvent?.pairingEventType === "mixer" ||
      (pairingEvent?.pairingEventType === "dinner" &&
        pairingEvent?.event?.status === "active")
    ) {
      setLoading(true);
      const body = { eventId: pairingEvent.event._id, filters };
      const result = await request(
        "pairingevent/calcmatchscores",
        "post",
        body
      );
      if (result.success) {
        setPairingData(result.data);
      }

      setLoading(false);
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      {loading ? (
        <CircularProgress style={{ width: 25, height: 25 }} />
      ) : (
        <Button
          variant="outlined"
          onClick={handleButtonClick}
          disabled={diableCreate}
          size={pairingEvent?.pairingEventType === "mixer" ? "small" : ""}
        >
          {buttonTitle}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {buttonTitle}
          <Tooltip
            title="Choose which filters you want to include as dealbreakers and then click the create button. Filters not selected will be considered 'nice to haves' and will simply contribute to a couple's match score."
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            arrow
          >
            <IconButton onClick={handleHelpClick}>
              <HelpOutline
                style={{ width: 20, marginLeft: 10, color: "grey" }}
              />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {filters ? (
            <Grid container direction="column" alignItems="center">
              <Grid item>
                <FormGroup>
                  {filters.map((filter, i) => {
                    if (filter?.variant === "age") {
                      return renderAgeFilter(filter, i);
                    } else {
                      return (
                        <FormControlLabel
                          key={filter._id}
                          control={
                            <Checkbox
                              checked={filter.checked ? filter.checked : false}
                              onChange={() => handleCheck(i)}
                            />
                          }
                          label={filter.label}
                        />
                      );
                    }
                  })}
                  {showMaximizeDates && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={maximizeDates}
                          onChange={() => setMaximizeDates(!maximizeDates)}
                          style={{ color: "#ef5241" }}
                        />
                      }
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color: "red",
                        },
                      }}
                      label="Balance Dates"
                    />
                  )}

                  {maximizeDates && (
                    <Typography variant="caption">
                      USE THIS CAREFULLY! This option attempts to give each
                      attendee has the same number of dates, even if it means
                      fewer matches overall. It is very possible that you will
                      end up not using all of the rounds.
                    </Typography>
                  )}
                  {showPrevMatches ? (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={prevMatches}
                            onChange={() => setPrevMatches(!prevMatches)}
                            style={{ color: "#ef5241" }}
                          />
                        }
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            color: "red",
                          },
                        }}
                        label="Allow Previous Matches"
                      />
                      {prevMatches ? (
                        <Typography variant="caption">
                          Checking this box will allow attendees who have met at
                          previous events to meet again at this one.
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </FormGroup>
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
          {showRefreshWarning ? (
            <Typography
              style={{ padding: 20, color: "red", fontWeight: "bold" }}
            >
              Heads up: Refreshing matches more than halfway through the event
              can lead to some participants receiving far fewer dates. It may be
              better to pair up people manually and create a wild card match.{" "}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
